/**
 * MarketStore — Zustand store fed by HydraClient
 *
 * Hydra sends two ticker formats:
 *   Kalshi:      KXNBAGAME-26APR04DETPHI-DET  (eventTicker: KXNBAGAME-26APR04DETPHI)
 *   Polymarket:  PM-nba-det-phi-2026-04-04     (moneyline)
 *                PM-nba-det-phi-2026-04-04-spread-home-1pt5
 *                PM-nba-det-phi-2026-04-04-total-139pt5
 *
 * Performance: Maps are mutated in-place. A `version` counter is bumped on a
 * RAF-throttled schedule so React re-renders at ~display refresh rate, not per-message.
 */

import { create } from 'zustand';
import type { HydraMarket, HydraOrderbook, HydraPriceLevel } from '../clients/HydraClient';
import { getTeamDisplayName } from '../lib/kalshiTicker/teamNames';
import type { GameRow, VenueBook, Sport, MarketType, BookLevel, SeriesEvent, SeriesOutcome, DateFilter, StatusFilter } from '../views/ValueDashboard/types';

// ── Ticker parsing ───────────────────────────────────────────────

interface ParsedTicker {
  source: 'kalshi' | 'polymarket';
  sport: Sport;
  date: string;
  awayCode: string;
  homeCode: string;
  outcomeCode: string;
  side: 'away' | 'home';
  marketType: 'moneyline' | 'spread' | 'total' | 'series';
  seriesYear?: string;   // only set when marketType === 'series'
}

const MONTHS: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

const SPORT_NORMALIZE: Record<string, Sport> = {
  NBA: 'nba', NHL: 'nhl', CBB: 'cbb', MLB: 'mlb', ATP: 'atp', WTA: 'wta', UFC: 'ufc',
  nba: 'nba', nhl: 'nhl', cbb: 'cbb', mlb: 'mlb', atp: 'atp', wta: 'wta', ufc: 'ufc',
};

function parseKalshiTicker(ticker: string, eventTicker: string): ParsedTicker | null {
  // Series (championship) shape: KXNBA-26-LAL / KXNHL-26-EDM / KXMLB-26-SEA
  // Matched on the full ticker (no GAME/MATCH/FIGHT suffix), before the generic path.
  const seriesChampMatch = ticker.match(/^KX([A-Z]+)-(\d{2})-([A-Z0-9]+)$/);
  if (seriesChampMatch) {
    const [, sportLetters, year, outcomeCode] = seriesChampMatch;
    const sport = SPORT_NORMALIZE[sportLetters];
    if (sport) {
      return {
        source: 'kalshi', sport,
        date: `20${year}-01-01`,
        awayCode: outcomeCode, homeCode: '',
        outcomeCode, side: 'away',
        marketType: 'series',
        seriesYear: year,
      };
    }
  }

  const seriesMatch = eventTicker.match(/^(KX[A-Z]+(?:GAME|MATCH|FIGHT|SPREAD|TOTAL))-(.+)$/);
  if (!seriesMatch) return null;
  const [, series, rest] = seriesMatch;

  const sportMap: Record<string, Sport> = {
    KXNBAGAME: 'nba', KXNHLGAME: 'nhl', KXNCAAMBGAME: 'cbb',
    KXMLBGAME: 'mlb', KXATPMATCH: 'atp', KXWTAMATCH: 'wta',
    KXUFCFIGHT: 'ufc',
    KXNBASPREAD: 'nba', KXNHLSPREAD: 'nhl', KXNCAAMBSPREAD: 'cbb',
    KXMLBSPREAD: 'mlb', KXATPSPREAD: 'atp', KXWTASPREAD: 'wta',
    KXNBATOTAL: 'nba', KXNHLTOTAL: 'nhl', KXNCAAMBTOTAL: 'cbb',
    KXMLBTOTAL: 'mlb', KXATPTOTAL: 'atp', KXWTATOTAL: 'wta',
  };
  const sport = sportMap[series];
  if (!sport) return null;

  const marketType: 'moneyline' | 'spread' | 'total' =
    series.endsWith('SPREAD') ? 'spread' : series.endsWith('TOTAL') ? 'total' : 'moneyline';
  // UFC FIGHT, tennis MATCH, and all GAME tickers parse as moneyline (1v1 outcome).

  // MLB tickers embed an HHMM start time between the date and the team codes
  // (e.g. KXMLBGAME-26APR131610HOUSEA-SEA where 1610 = 16:10 ET).
  const dateMatch = rest.match(/^(\d{2})([A-Z]{3})(\d{2})(\d{4})?([A-Z].+)$/);
  if (!dateMatch) return null;
  const [, yy, mmm, dd, , teamsPart] = dateMatch;
  const mm = MONTHS[mmm];
  if (!mm) return null;
  const date = `20${yy}-${mm}-${dd}`;

  const tickerParts = ticker.split('-');
  const outcomeCode = tickerParts[tickerParts.length - 1];
  const outcomeIdx = teamsPart.indexOf(outcomeCode);
  let awayCode: string, homeCode: string;

  if (outcomeIdx === 0) {
    awayCode = outcomeCode;
    homeCode = teamsPart.slice(outcomeCode.length);
  } else if (outcomeIdx > 0) {
    awayCode = teamsPart.slice(0, outcomeIdx);
    homeCode = outcomeCode;
  } else {
    const mid = Math.ceil(teamsPart.length / 2);
    awayCode = teamsPart.slice(0, mid);
    homeCode = teamsPart.slice(mid);
  }
  if (!awayCode || !homeCode) return null;

  // Determine side: moneyline outcomes are exact team codes (DET),
  // spread outcomes are team+number (DET1, MIA33), totals are just numbers (215)
  let side: 'away' | 'home';
  if (marketType === 'total') {
    side = 'away'; // totals aren't team-specific
  } else if (outcomeCode === awayCode) {
    side = 'away';
  } else if (outcomeCode === homeCode) {
    side = 'home';
  } else {
    // Spread: outcome starts with team code (DET1 → DET)
    side = outcomeCode.startsWith(awayCode) ? 'away' : 'home';
  }

  return {
    source: 'kalshi', sport, date,
    awayCode: awayCode.toUpperCase(), homeCode: homeCode.toUpperCase(),
    outcomeCode: outcomeCode.toUpperCase(),
    side,
    marketType,
  };
}

function parsePolyTicker(ticker: string): ParsedTicker | null {
  const match = ticker.match(/^PM-([a-z]+)-([a-z]+)-([a-z]+)-(\d{4}-\d{2}-\d{2})(.*)$/);
  if (!match) return null;
  const [, sportRaw, awayRaw, homeRaw, date, suffix] = match;
  const sport = SPORT_NORMALIZE[sportRaw];
  if (!sport) return null;

  let marketType: 'moneyline' | 'spread' | 'total' = 'moneyline';
  if (suffix.startsWith('-spread')) marketType = 'spread';
  else if (suffix.startsWith('-total')) marketType = 'total';

  return {
    source: 'polymarket', sport, date,
    awayCode: awayRaw.toUpperCase(), homeCode: homeRaw.toUpperCase(),
    outcomeCode: awayRaw.toUpperCase(), side: 'away',
    marketType,
  };
}

function parseTicker(market: HydraMarket): ParsedTicker | null {
  if (market.source === 'polymarket' || market.ticker.startsWith('PM-')) {
    return parsePolyTicker(market.ticker);
  }
  if (market.eventTicker) return parseKalshiTicker(market.ticker, market.eventTicker);
  return null;
}

// ── Helpers ──────────────────────────────────────────────────────

function toBookLevels(levels: HydraPriceLevel[]): BookLevel[] {
  return levels
    .map((l) => ({ priceCents: l.price, size: l.size }))
    .sort((a, b) => b.priceCents - a.priceCents); // descending: best bid first
}

function centsToAmerican(cents: number): number {
  if (cents <= 0 || cents >= 100) return 0;
  if (cents >= 50) return Math.round((-100 * cents) / (100 - cents));
  return Math.round((100 * (100 - cents)) / cents);
}

// ── Feed log (ring buffer, last 100 pricing updates) ─────────────

export interface FeedLogEntry {
  ts: number;          // Date.now()
  ticker: string;
  source: 'kalshi' | 'polymarket';
  sport: string;
  type: 'market' | 'orderbook';
  yesBid?: number;
  yesAsk?: number;
  volume?: number;     // market volume (from HydraMarket.volume)
  bookLevels?: number; // total yes+no levels in orderbook
}

const FEED_LOG_MAX = 100;
const _feedLogKalshi: FeedLogEntry[] = [];
const _feedLogPoly: FeedLogEntry[] = [];

function pushFeedLog(entry: FeedLogEntry) {
  const log = entry.source === 'kalshi' ? _feedLogKalshi : _feedLogPoly;
  log.push(entry);
  if (log.length > FEED_LOG_MAX) log.shift();
}

// ── Mutable data layer (not in Zustand state — no copy overhead) ─

const _markets = new Map<string, HydraMarket>();
const _orderbooks = new Map<string, HydraOrderbook>();
let _dirty = false;
let _rafId: number | null = null;
let _flushFn: (() => void) | null = null;

function scheduleTick() {
  if (_rafId !== null) return;
  _rafId = requestAnimationFrame(() => {
    _rafId = null;
    if (_dirty && _flushFn) {
      _dirty = false;
      _flushFn();
    }
  });
}

// ── Store ────────────────────────────────────────────────────────

interface GameAccum {
  sport: Sport; date: string; awayCode: string; homeCode: string; title: string;
  kalshiAwayTicker: string | null; kalshiHomeTicker: string | null;
  polyTicker: string | null;
}

interface MarketState {
  version: number;       // Bumped on RAF tick — triggers re-render
  connected: boolean;
  marketsCount: number;
  orderbooksCount: number;

  ingestMarket: (market: HydraMarket) => void;
  ingestOrderbook: (ticker: string, book: HydraOrderbook) => void;
  setConnected: (connected: boolean) => void;
  getGames: (
    sport: Sport,
    marketType: MarketType,
    filters?: { statusFilter?: StatusFilter; dateFilter?: DateFilter; selectedDateIso?: string }
  ) => GameRow[];
  getSeries: (sport: Sport) => SeriesEvent | null;
  getFeedLog: (source?: 'kalshi' | 'polymarket') => FeedLogEntry[];
  getMarketPrice: (ticker: string) => number | null;
}

const SERIES_TITLE: Record<string, string> = {
  nba: 'NBA Champion',
  nhl: 'Stanley Cup',
  mlb: 'World Series',
};

export const useMarketStore = create<MarketState>((set, get) => {
  // Wire the flush function to bump version
  _flushFn = () => {
    set({ version: get().version + 1, marketsCount: _markets.size, orderbooksCount: _orderbooks.size });
  };

  return {
    version: 0,
    connected: false,
    marketsCount: 0,
    orderbooksCount: 0,

    ingestMarket: (market) => {
      _markets.set(market.ticker, market);
      pushFeedLog({
        ts: Date.now(),
        ticker: market.ticker,
        source: market.source,
        sport: market.sport || '?',
        type: 'market',
        yesBid: market.yesBid,
        yesAsk: market.yesAsk,
        volume: market.volume,
      });
      _dirty = true;
      scheduleTick();
    },

    ingestOrderbook: (ticker, book) => {
      _orderbooks.set(ticker, book);

      // Derive price from orderbook (Hydra sends book updates far more often than market updates)
      const bestYesBid = book.yes?.length ? Math.max(...book.yes.map(l => l.price)) : null;
      const bestNoBid = book.no?.length ? Math.max(...book.no.map(l => l.price)) : null;
      const yesAsk = bestNoBid !== null ? 100 - bestNoBid : null;
      const existing = _markets.get(ticker);
      if (existing && (bestYesBid !== null || yesAsk !== null)) {
        existing.yesBid = bestYesBid ?? existing.yesBid;
        existing.yesAsk = yesAsk ?? existing.yesAsk;
        existing.noBid = bestNoBid ?? existing.noBid;
        existing.noAsk = bestYesBid !== null ? 100 - bestYesBid : existing.noAsk;
        existing.lastUpdateMs = Date.now();
      }

      pushFeedLog({
        ts: Date.now(),
        ticker,
        source: ticker.startsWith('PM-') ? 'polymarket' : 'kalshi',
        sport: existing?.sport || '?',
        type: 'orderbook',
        yesBid: bestYesBid ?? undefined,
        yesAsk: yesAsk ?? undefined,
        bookLevels: (book.yes?.length ?? 0) + (book.no?.length ?? 0),
      });
      _dirty = true;
      scheduleTick();
    },

    setConnected: (connected) => set({ connected }),

    getGames: (sport: Sport, marketType: MarketType = 'moneyline', filters): GameRow[] => {
      // Force dependency on version so this re-derives on tick
      void get().version;

      const statusFilter = filters?.statusFilter ?? 'active';
      const dateFilter = filters?.dateFilter ?? 'today';
      const selectedDateIso = filters?.selectedDateIso;

      // Pass 1: Group by game. Date filter applies at parse-time (cheap); status
      // filter defers to Pass 3 since we need to see all markets for the game
      // before deciding if any are still open.
      const games = new Map<string, GameAccum>();

      for (const [, market] of _markets) {
        const parsed = parseTicker(market);
        if (!parsed || parsed.sport !== sport || parsed.marketType !== marketType) continue;

        if (dateFilter === 'today' && selectedDateIso && parsed.date !== selectedDateIso) continue;

        // Prefer Hydra's canonical game key when present so cross-venue alias
        // mismatches (Kalshi CHW vs Polymarket CWS for White Sox) collapse
        // into one row. Fall back to the parsed key for older Hydra builds.
        const key = market.canonicalGameKey
          ?? `${parsed.sport}|${parsed.date}|${parsed.awayCode}|${parsed.homeCode}`;
        if (!games.has(key)) {
          games.set(key, {
            sport: parsed.sport, date: parsed.date,
            awayCode: parsed.awayCode, homeCode: parsed.homeCode,
            title: market.title || '',
            kalshiAwayTicker: null, kalshiHomeTicker: null, polyTicker: null,
          });
        }
        const g = games.get(key)!;
        if (parsed.source === 'kalshi') {
          if (parsed.side === 'away') g.kalshiAwayTicker = market.ticker;
          else g.kalshiHomeTicker = market.ticker;
        } else {
          g.polyTicker = market.ticker;
        }
      }

      // Pass 2: Build rows
      const rows: GameRow[] = [];

      for (const [key, g] of games) {
        const kAwayMkt = (g.kalshiAwayTicker ? _markets.get(g.kalshiAwayTicker) : null) ?? null;
        const kHomeMkt = (g.kalshiHomeTicker ? _markets.get(g.kalshiHomeTicker) : null) ?? null;
        const kAwayBook = (g.kalshiAwayTicker ? _orderbooks.get(g.kalshiAwayTicker) : null) ?? null;
        const kHomeBook = (g.kalshiHomeTicker ? _orderbooks.get(g.kalshiHomeTicker) : null) ?? null;
        const polyMkt = (g.polyTicker ? _markets.get(g.polyTicker) : null) ?? null;
        const polyBook = (g.polyTicker ? _orderbooks.get(g.polyTicker) : null) ?? null;

        function buildVenueBook(mkt: HydraMarket | null, book: HydraOrderbook | null): VenueBook {
          const price = mkt?.yesBid != null && mkt?.yesAsk != null
            ? Math.round((mkt.yesBid + mkt.yesAsk) / 2) : mkt?.lastPrice ?? null;
          const bids = book?.yes ?? [];
          const asks = book?.no ?? [];
          const liq = bids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
          // Use market volume if Hydra sends it; otherwise derive from orderbook depth
          const bookDepth = bids.reduce((s, l) => s + l.size, 0) + asks.reduce((s, l) => s + l.size, 0);
          const vol = mkt?.volume ?? bookDepth;
          return {
            marketTicker: mkt?.ticker ?? '', priceCents: price,
            americanOdds: price ? centsToAmerican(price) : null,
            volume: vol, liquidity: Math.round(liq),
            yes: toBookLevels(bids), no: toBookLevels(asks),
          };
        }

        function buildPolyBooks(): { away: VenueBook; home: VenueBook } | null {
          if (!polyMkt) return null;
          const awayPrice = polyMkt.yesBid != null && polyMkt.yesAsk != null
            ? Math.round((polyMkt.yesBid + polyMkt.yesAsk) / 2) : polyMkt.lastPrice ?? null;
          const homePrice = awayPrice != null ? 100 - awayPrice : null;
          const vol = polyMkt.volume ?? 0;
          const awayBids = polyBook?.yes ?? [];
          const awayAsks = polyBook?.no ?? [];
          const awayLiq = awayBids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
          const homeLiq = awayAsks.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);

          return {
            away: {
              marketTicker: polyMkt.ticker, priceCents: awayPrice,
              americanOdds: awayPrice ? centsToAmerican(awayPrice) : null,
              volume: vol, liquidity: Math.round(awayLiq),
              yes: toBookLevels(awayBids), no: toBookLevels(awayAsks),
            },
            home: {
              marketTicker: polyMkt.ticker, priceCents: homePrice,
              americanOdds: homePrice ? centsToAmerican(homePrice) : null,
              volume: vol, liquidity: Math.round(homeLiq),
              // CHA-NO bid == ORL-YES bid (same outcome token, same price) — no 100-x flip.
              yes: toBookLevels(awayAsks),
              no: toBookLevels(awayBids),
            },
          };
        }

        if (statusFilter !== 'all') {
          const statuses = [kAwayMkt?.status, kHomeMkt?.status, polyMkt?.status].filter(Boolean) as string[];
          if (statuses.length > 0) {
            const anyOpen = statuses.some(s => s === 'open');
            if (statusFilter === 'active' && !anyOpen) continue;
            if (statusFilter === 'settled' && anyOpen) continue;
          }
        }

        // Prefer Kalshi's canonical codes for display (Poly leaves them
        // undefined because its slug order is YES-outcome, not game-day).
        const awayCode = kAwayMkt?.canonicalAwayCode ?? kHomeMkt?.canonicalAwayCode ?? g.awayCode;
        const homeCode = kAwayMkt?.canonicalHomeCode ?? kHomeMkt?.canonicalHomeCode ?? g.homeCode;

        rows.push({
          key, sport: g.sport, date: g.date, timePt: '',
          awayCode, homeCode,
          awayName: getTeamDisplayName(awayCode, g.sport), homeName: getTeamDisplayName(homeCode, g.sport),
          kalshi: (kAwayMkt || kHomeMkt) ? { away: buildVenueBook(kAwayMkt, kAwayBook), home: buildVenueBook(kHomeMkt, kHomeBook) } : null,
          polymarket: buildPolyBooks(),
        });
      }

      rows.sort((a, b) => a.date.localeCompare(b.date) || a.awayCode.localeCompare(b.awayCode));
      return rows;
    },

    getSeries: (sport: Sport): SeriesEvent | null => {
      void get().version;
      const outcomes: SeriesOutcome[] = [];
      let year: string | null = null;
      let seriesTicker: string | null = null;

      for (const [, market] of _markets) {
        const parsed = parseTicker(market);
        if (!parsed || parsed.sport !== sport || parsed.marketType !== 'series') continue;
        year ??= parsed.seriesYear ?? null;
        // Derive KX{SPORT} prefix from the ticker itself (everything before first dash)
        seriesTicker ??= market.ticker.split('-')[0];

        const book = _orderbooks.get(market.ticker) ?? null;
        const price = market.yesBid != null && market.yesAsk != null
          ? Math.round((market.yesBid + market.yesAsk) / 2) : market.lastPrice ?? null;
        const bids = book?.yes ?? [];
        const asks = book?.no ?? [];
        const liq = bids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
        const bookDepth = bids.reduce((s, l) => s + l.size, 0) + asks.reduce((s, l) => s + l.size, 0);
        const vol = market.volume ?? bookDepth;

        outcomes.push({
          ticker: market.ticker,
          code: parsed.outcomeCode,
          name: getTeamDisplayName(parsed.outcomeCode, sport),
          kalshi: {
            marketTicker: market.ticker,
            priceCents: price,
            americanOdds: price ? centsToAmerican(price) : null,
            volume: vol, liquidity: Math.round(liq),
            yes: toBookLevels(bids), no: toBookLevels(asks),
          },
        });
      }

      if (outcomes.length === 0 || !year || !seriesTicker) return null;

      // Sort by priceCents desc (favorites first), unpriced last
      outcomes.sort((a, b) => {
        const pa = a.kalshi?.priceCents ?? -1;
        const pb = b.kalshi?.priceCents ?? -1;
        return pb - pa;
      });

      return {
        key: `${sport}|${year}`,
        sport,
        year,
        seriesTicker,
        title: `${SERIES_TITLE[sport] ?? sport.toUpperCase()} 20${year}`,
        outcomes,
      };
    },

    getFeedLog: (source?: 'kalshi' | 'polymarket'): FeedLogEntry[] => {
      void get().version;
      if (source === 'kalshi') return [..._feedLogKalshi].reverse();
      if (source === 'polymarket') return [..._feedLogPoly].reverse();
      // Both merged, newest first
      return [..._feedLogKalshi, ..._feedLogPoly]
        .sort((a, b) => b.ts - a.ts)
        .slice(0, FEED_LOG_MAX);
    },

    getMarketPrice: (ticker: string): number | null => {
      void get().version; // re-derive on price updates
      const market = _markets.get(ticker);
      if (!market) return null;
      if (market.yesBid != null && market.yesAsk != null) {
        return Math.round((market.yesBid + market.yesAsk) / 2);
      }
      return market.lastPrice ?? null;
    },
  };
});
